import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { sameSession } from "../auth/session";
import { useSessionKey } from "../auth/useSessionKey";
import { logAction } from "../log/actionLog";
import { FieldRow, Remaining } from "../funnel/Champ";
import { visibleFields } from "../funnel/conditions";
import {
  isEmail,
  allInvalid,
  emailRefusal,
  type Complaint,
} from "../funnel/checks";
import {
  changesAnything,
  fetchAnswers,
  saveAnswers,
  valueOf,
  lockedOf,
} from "../funnel/forms";
import { useChosenForm, useWords } from "../funnel/useForm";
import { useFunnelNav } from "../funnel/useFunnelNav";
import { REPEATABLE, instanceKey, instancePath } from "../funnel/order";
import { useParticipation } from "../funnel/useParticipation";
import { useMode } from "../panel/mode";
import { fallbackOf } from "../settings/catalog";
import { useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";

/**
 * REGISTRATION — THE PARTICIPANT'S DETAILS.
 *
 * ⚠️ THE FORM COMES FROM THE BACK OFFICE. A setting names which of the event's forms
 * this step serves; its fields, their order and what is required are decided there.
 * The prototype invents a field builder because it has no database behind it — built
 * here, it would be a second definition of every field, free to drift from the one
 * the back office already holds and the badges are printed from.
 *
 * ⚠️ THE ANSWERS ARE WRITTEN HERE, not at the confirmation. Unlike the participation,
 * these are the participant's own record — and a form filled in and then lost on the
 * way to the next step is the kind of thing people do not fill in twice. `extended`
 * is merged, never replaced: the bag holds other forms' answers too.
 *
 * Le RENDU d'un champ, lui, n'est plus ici : l'étape société sert le même genre de
 * formulaire, et deux copies auraient divergé — voir `funnel/Champ.tsx`.
 *
 * Displayed strings are French: this is a French product.
 */

const ICI = "/registration/informations";

/**
 * ═══ CET ÉCRAN EST SERVI PLUSIEURS FOIS ═══
 *
 * Un événement peut demander deux formulaires — l'identité, puis des questions propres à
 * l'édition. Chaque instance a donc SES réglages : son titre, son sous-titre, son bloc
 * libre et le formulaire qu'elle sert.
 *
 * ⚠️ LA PREMIÈRE N'EST PAS SUFFIXÉE. `instanceKey` le dit : tout ce qui a été réglé avant
 * cette fonctionnalité vit sous `FO_MEETING_V2_INFO_*`, et suffixer la première aurait
 * rendu ces valeurs orphelines. L'adresse suit la même règle — `/registration/informations`
 * reste celle de la première.
 */
function useInstance(): string | undefined {
  return useParams().instance;
}

/**
 * ═══ ET IL EST AUSSI SERVI COMME PAGE ═══
 *
 * `/informations`, dans le menu ou en tuile : on revient sur ses réponses sans
 * retraverser l'inscription. C'est LE MÊME écran — même formulaire, mêmes réglages, même
 * nom — monté ailleurs.
 *
 * ⚠️ CE QUI CHANGE TIENT EN TROIS POINTS, ET AUCUN N'EST COSMÉTIQUE : il n'y a pas
 * d'étape suivante (donc « Continuer » n'a rien à continuer), il n'y a pas d'étape
 * précédente à proposer, et le bouton doit dire ce qu'il fait — enregistrer. Le succès
 * se dit sur place, puisqu'on n'en bouge plus : sans ce mot, on ne saurait jamais si le
 * bouton a servi à quelque chose.
 *
 * ⚠️ ET L'ENREGISTREMENT, LUI, NE CHANGE PAS D'UN IOTA. C'est ce qui rend cette page
 * possible : cet écran écrit ses réponses LUI-MÊME (`saveAnswers`), il n'attend pas la
 * confirmation — à la différence des dates ou de la billetterie, qui ne posent les
 * leurs qu'à la fin et ne peuvent donc pas sortir du tunnel.
 */
export function Informations({ asPage }: { asPage?: true }) {
  const t = useT();
  const event = useEvent();
  const client = useQueryClient();
  const { me } = useParticipation();
  /* Sous quelle session cet écran est composé : voir `submit`. */
  const sessionKey = useSessionKey();

  /* Les réglages de CETTE instance. Le repli, lui, reste celui de la clé de base : le
     catalogue ne déclare qu'une fois « Vos informations », et une seconde étape sans titre
     écrit ne doit pas s'afficher sans titre du tout. */
  const instance = useInstance();
  const key = (base: string) => instanceKey(base, instance);

  const title = useText(
    key("FO_MEETING_V2_INFO_TITLE"),
    fallbackOf("FO_MEETING_V2_INFO_TITLE"),
  );
  const subtitle = useText(
    key("FO_MEETING_V2_INFO_SUBTITLE"),
    fallbackOf("FO_MEETING_V2_INFO_SUBTITLE"),
  );
  const html = useText(key("FO_MEETING_V2_INFO_HTML"));

  /* Le formulaire que cette étape sert, et ses libellés dans la langue lue — voir
     `funnel/useForm.ts`, où les trois écrans qui servent un formulaire du back office
     partagent leurs deux lectures. */
  const form = useChosenForm(key("FO_MEETING_V2_INFO_FORM_ID"));
  const words = useWords(form);
  const organiser = useMode() === "organiser";

  /** What this participant has already answered — the form opens on it. */
  const stored = useQuery({
    queryKey: ["form-answers", event?.domainId, event?.eventId, me?.id],
    queryFn: ({ signal }) =>
      fetchAnswers(
        event!.domainId,
        { eventId: event!.eventId, userId: me!.id },
        signal,
      ),
    enabled: !!event && typeof me?.id === "number",
    staleTime: Infinity,
    retry: false,
  });

  /**
   * What has been TYPED, over what the server holds. Derived rather than seeded:
   * the answers arrive asynchronously, and a `useState` initialiser runs once —
   * before them — so the form would stay empty over answers that exist.
   */
  const [typed, setTyped] = useState<Record<string, unknown>>({});
  const fields = form?.fields ?? [];
  const values = Object.fromEntries(
    fields.map((f) => [
      f.id,
      f.id in typed ? typed[f.id] : valueOf(stored.data ?? {}, f),
    ]),
  );

  /* Ce que le back office a figé, sur ce que le serveur détient déjà — voir
     `verrouillesDe`, qui porte la règle et sa subtilité. */
  const locked = lockedOf(fields, stored.data ?? {});

  /**
   * ═══ Y A-T-IL QUELQUE CHOSE À REMPLIR ? ═══
   *
   * ⚠️ TROIS CONDITIONS, PAS UNE. Le formulaire peut manquer (aucun choisi, ou supprimé
   * depuis), ses libellés peuvent ne pas être arrivés, et il peut EXISTER sans porter une
   * seule question servable — `fieldOf` écarte les lignes sans composant ni clé, et la
   * route rend aussi les formulaires de domaine de la V1, dont certains n'ont aucune ligne.
   *
   * Ce qui en dépend : le formulaire n'est pas rendu vide, l'alerte de diagnostic couvre
   * les trois cas, et surtout une sortie existe toujours (voir plus bas). Sans elle,
   * l'étape était un CUL-DE-SAC : l'alerte s'affichait seule, et la rangée de boutons,
   * enfermée dans le même test que le formulaire, disparaissait avec lui — plus rien à
   * cliquer, et sur téléphone pas même le fil d'Ariane, qui est réservé au grand écran.
   */
  const servable = !!form && !!words && fields.length > 0;

  /**
   * CE QUI EMPÊCHE ENCORE L'ENVOI — calculé au rendu, et non plus au seul clic.
   *
   * ⚠️ C'EST CE QUI FAIT VIVRE LA PHRASE DU BAS. Adossée aux reproches mis en état par le
   * clic, elle aurait encore nommé un champ qu'on vient de remplir. Recalculée, elle ne
   * nomme que ceux qui manquent encore, et disparaît d'elle-même quand il n'y en a plus.
   */
  const invalid = allInvalid(fields, (f) => values[f.id], locked);

  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  /** Servie en page : « c'est enregistré », puisqu'on ne va nulle part. */
  const [saved, setSaved] = useState(false);
  /** Les réponses qu'on refuse et pourquoi — d'ici, ou de la bouche du serveur. */
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  /**
   * A-T-ON DÉJÀ ESSAYÉ D'ENVOYER ? C'est ce qui autorise la phrase du bas.
   *
   * ⚠️ ELLE NE SE DIT PAS À L'ARRIVÉE. Un formulaire vierge est incomplet par définition :
   * annoncer « il reste à remplir » avant qu'on ait touché quoi que ce soit, c'est
   * reprocher de ne pas avoir rempli ce qu'on vient d'ouvrir.
   */
  const [tried, setTried] = useState(false);
  /* ⚠️ L'ADRESSE DE CETTE INSTANCE, ET NON CELLE DE LA FAMILLE. Le fil se lit par
     chemin : donné le chemin de base, la deuxième étape « informations » aurait cru
     être la première, et « Continuer » l'aurait renvoyée là où la première mène. */
  const { previous, next, go } = useFunnelNav(instancePath(ICI, instance));

  /** Chaque reproche sous son champ. Le premier peut être hors écran : le focus l'y ramène. */
  const show = (rs: Complaint[]) => {
    setComplaints(rs);
    setFailed(false);
    if (rs[0]) document.getElementById(`f-${rs[0].id}`)?.focus();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || !event || typeof me?.id !== "number" || (!asPage && !next))
      return;
    /**
     * ⚠️ AUCUN ENVOI SI LA SESSION A CHANGÉ depuis le rendu de cet écran (bug 8, chasse du
     * 2026-10-20). Un autre onglet a pu ouvrir un autre compte : ce qui est tapé ici est à
     * l'ancien, et `me.id` — relu entre-temps — serait déjà celui du nouveau. L'envoi
     * écrasait la fiche d'un autre. Le `Shell` démonte l'écran et le dit (voir `Shell.tsx`).
     */
    if (!sameSession(sessionKey)) return;
    setComplaints([]);
    setFailed(false);
    setSaved(false);
    // À partir d'ici, la phrase du bas a le droit de parler : on a essayé.
    setTried(true);

    /**
     * ⚠️ CE QUI NE PEUT PAS EXISTER EST ARRÊTÉ AVANT LE VOYAGE — voir `verifie`.
     * `required` ne voit qu'un champ vide : tout le reste (« 06 12 », « jean@ »)
     * le remplit, part, et ne revenait qu'en « Réessayez ».
     */
    if (invalid.length > 0) {
      show(invalid.map((i) => i.complaint));
      return;
    }

    setSaving(true);
    try {
      /**
       * ⚠️ RIEN À ÉCRIRE, RIEN À JOURNALISER quand les réponses sont celles du serveur.
       * Repasser par ses informations sans rien changer réécrivait les mêmes valeurs et
       * journalisait une « mise à jour » à chaque passage. `stored.data` absent = on n'a
       * rien lu : on écrit, comme avant.
       */
      const current = stored.data;
      if (!current || changesAnything({ fields, values, current, locked })) {
        await saveAnswers({
          domainId: event.domainId,
          target: { eventId: event.eventId, userId: me.id },
          fields,
          values,
          current: current ?? {},
          locked,
        });
        logAction(event, "userUpdate");
      }

      /**
       * ⚠️ CE QUI EST EN CACHE VIENT D'ÊTRE PÉRIMÉ PAR NOTRE PROPRE ÉCRITURE, et sans
       * ça l'écran mentait au retour. `staleTime: Infinity` empêche toute relecture
       * spontanée — c'est voulu, ces réponses ne changent pas toutes seules — mais
       * elles viennent de changer ICI. Le participant enregistrait, passait à la
       * confirmation, revenait sur ses informations, et retrouvait ses anciennes
       * réponses : la saisie n'était plus dans `typed` (l'écran a été démonté), donc
       * elle repartait du cache, resté sur l'état d'avant. Seul un rechargement complet
       * remettait les choses d'aplomb.
       *
       * `refetchType: "none"` : on MARQUE périmé sans relire tout de suite. On s'en va
       * à l'instant même, une requête lancée ici serait annulée au démontage ; la
       * relecture se fait au retour, quand quelqu'un regarde.
       */
      await client.invalidateQueries({
        queryKey: ["form-answers", event.domainId, event.eventId, me.id],
        refetchType: "none",
      });

      /**
       * ⚠️ ET LA FICHE DE LA PERSONNE, parce que ce formulaire écrit dans le modèle
       * `U` — nom, prénom, adresse électronique. Ce sont exactement les champs que
       * porte `Me` : la pastille du compte, le nom du badge et le récapitulatif les
       * lisent. Non périmés, corriger son nom ici le laissait affiché à l'ancien
       * partout ailleurs jusqu'au prochain rechargement complet.
       *
       * Même précaution qu'au-dessus : on marque, on ne relit pas — dans le tunnel on
       * quitte l'écran à l'instant, et la requête serait annulée au démontage.
       */
      await client.invalidateQueries({
        queryKey: ["participant"],
        refetchType: "none",
      });
      /* ⚠️ ET LA PARTICIPATION : ce formulaire écrit dans `UE.extended` (fusion côté
         apix), et la confirmation renvoie ensuite `{...recorded.extended}` à api-v1,
         qui REMPLACE la colonne. Non périmée, la réponse venait d'être effacée. */
      await client.invalidateQueries({
        queryKey: ["participation"],
        refetchType: "none",
      });

      /* En page, on reste : le mot qui suit est la seule preuve que le bouton a servi.
         Dans le tunnel, l'écran d'après est cette preuve. */
      if (asPage) setSaved(true);
      else if (next) go(next.path);
    } catch (error) {
      /**
       * ⚠️ L'ADRESSE REFUSÉE N'EST PAS UNE PANNE, et le bandeau générique en faisait
       * une : « Réessayez » devant une adresse déjà prise ou sur un domaine jetable
       * envoie quelqu'un rappuyer sur un bouton qui répondra la même chose à chaque
       * fois, sans jamais lui dire QUEL champ, ni QUOI corriger.
       *
       * Le champ visé est celui que le serveur a examiné : le `U.email`, le seul qu'il
       * passe par ses `Authentications`. Verrouillé, il n'a pas été envoyé, donc le
       * refus vient d'ailleurs et le bandeau reprend la main.
       */
      const field = fields.find(
        (f) => isEmail(f) && f.model === "U" && !locked.has(f.id),
      );
      const message = field ? emailRefusal(error) : null;
      if (field && message) show([{ id: field.id, message }]);
      else setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    /* `data-page` : l'instance, qui borne une règle d'apparence à CETTE étape — voir
       `selectorOf`. Servie en page ou dans le tunnel, c'est le même écran : la même. */
    <section className="page" data-page={instanceKey(REPEATABLE, instance)}>
      <div className="ttl" data-cfg="infoTitle">
        <InPlace settingKey={key("FO_MEETING_V2_INFO_TITLE")} label="Titre" text={title} as="h2" />
        <InPlace settingKey={key("FO_MEETING_V2_INFO_SUBTITLE")} label="Sous-titre" text={subtitle} as="p" />
      </div>

      {html.trim() !== "" && (
        <Prose
          html={html}
          className="se-prose"
          data-cfg="infoTitle"
          />
      )}

      {/* ⚠️ NO FORM CHOSEN, ONE THAT NO LONGER EXISTS, OR ONE WITH NOTHING TO ASK. Said,
          rather than an empty step somebody cannot get past — and said to the organiser,
          since they are the only one who can fix it. ⚠️ TO THEM ONLY: a participant has
          no form to choose and no journey to edit. For them the step is simply passed
          through, with the buttons below. */}
      <div className="alert" data-cfg="infoForm" hidden={servable || !organiser}>
        <span className="alert__d" aria-hidden="true">
          <i />
        </span>
        <span>
          <b>{form ? "Formulaire sans question" : "Aucun formulaire"}</b>
          <small>
            {form
              ? "Le formulaire choisi n'a aucune question servable : choisissez-en un autre, ou retirez l'étape du parcours."
              : "Choisissez celui que cette étape doit servir, ou retirez l'étape du parcours."}
          </small>
        </span>
      </div>

      {servable && (
        <form
          className="form"
          data-cfg="infoForm"
          /**
           * ⚠️ `noValidate` : LE REPROCHE EST CELUI DE L'ÉCRAN, PAS LA BULLE DU
           * NAVIGATEUR. Les deux coexistaient, et mal — le navigateur arrêtait les
           * champs de saisie avec sa propre bulle, dans sa propre langue, tandis que les
           * groupes de choix ne portent aucun `required` qu'il sache lire et passaient
           * sans rien dire. Un seul mécanisme désormais : `verifie` juge TOUS les
           * champs, chaque message s'écrit sous celui qu'il vise, et le focus va au premier.
           */
          noValidate
          onSubmit={submit}
        >
          {/* Seuls les champs que leurs conditions laissent voir : `conditions.ts`. */}
          {visibleFields(fields, (f) => values[f.id]).map((field) => (
            <FieldRow
              key={field.id}
              field={field}
              value={values[field.id]}
              locked={locked.has(field.id)}
              words={words}
              error={complaints.find((r) => r.id === field.id)?.message}
              onChange={(v) => {
                setTyped((t) => ({ ...t, [field.id]: v }));
                // Le reproche tombe dès qu'on retouche le champ visé, pas les autres.
                setComplaints((rs) => rs.filter((r) => r.id !== field.id));
              }}
            />
          ))}

          {/* A failure is SAID. Silent, it sends somebody on believing their details
              were recorded, and the next screen has no way of knowing they were not. */}
          {failed && (
            <p className="f--wide loginerr" role="alert">
              {t("DETAILS_NOT_SAVED", "Vos informations n'ont pas pu être enregistrées. Réessayez.")}
            </p>
          )}

          {/* ⚠️ DIT, ET SUR LA MÊME LIGNE QUE L'ÉCHEC : servi en page, on ne change pas
              d'écran, donc rien d'autre ne prouve que l'enregistrement a eu lieu. */}
          {asPage && saved && (
            <p className="f--wide step__ok" role="status">
              {t("YOUR_DETAILS_SAVED", 'Vos informations sont enregistrées.')}
            </p>
          )}

          {/**
           * ⚠️ CE QUE LE BOUTON NE DIT PAS. Il reste cliquable, et c'est voulu : le clic
           * EST le diagnostic (il écrit le reproche sous chaque champ fautif et amène le
           * focus sur le premier). Mais après ce clic, l'œil revient au bouton, où plus
           * rien n'explique pourquoi on n'a pas avancé — les messages, eux, sont restés
           * plus haut. Cette phrase les nomme tous, là où l'on regarde. Voir `leftToDo`.
           */}
          {tried && invalid.length > 0 && (
            <p className="f--wide hint" role="status">
              <Remaining invalid={invalid} words={words} />
            </p>
          )}

          <div className="actions f--wide">
            {/* ⚠️ HORS DU TUNNEL, RIEN NE RAMÈNE. Le fil d'Ariane n'est pas là, et une
                page qui n'offre que « Enregistrer » laisse au menu du haut le soin de la
                quitter — sur un téléphone, il est en bas et derrière un onglet. Le badge
                et la confirmation posent le même bouton au même endroit. */}
            {asPage && (
              <Button type="button" variant="ghost" onClick={() => go("/")}>
                {t("BACK_TO_HOME", "Retour à l'accueil")}
              </Button>
            )}
            {!asPage && previous && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => go(previous.path)}
              >
                {t("BACK", "Retour")}
              </Button>
            )}
            <Button
              type="submit"
              data-cfg="infoForm"
              loading={saving}
              disabled={!asPage && !next}
            >
              {asPage ? t("SAVE", "Enregistrer") : t("CONTINUE", "Continuer")}
            </Button>
          </div>
        </form>
      )}

      {/**
       * ⚠️ UNE ÉTAPE QU'ON NE PEUT PAS REMPLIR DOIT QUAND MÊME SE QUITTER. Rien à saisir
       * n'est pas une raison de retenir quelqu'un : l'étape est sautée, et le parcours
       * continue. C'est l'état par défaut de tout événement pas encore repris en V2
       * (l'étape est servie, `FO_MEETING_V2_INFO_FORM_ID` n'a pas de valeur par défaut),
       * donc le chemin le plus fréquent, pas un cas de bord.
       */}
      {!servable && (
        <div className="actions">
          {asPage && (
            <Button type="button" variant="ghost" onClick={() => go("/")}>
              {t("BACK_TO_HOME", "Retour à l'accueil")}
            </Button>
          )}
          {!asPage && previous && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => go(previous.path)}
            >
              {t("BACK", "Retour")}
            </Button>
          )}
          {!asPage && next && (
            <Button type="button" onClick={() => go(next.path)}>
              {t("CONTINUE", "Continuer")}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
